import { useRef, useState } from "react";
import { Sparkles, Loader2, CheckCircle2, AlertTriangle, ShieldCheck, Info, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// Optional AI add-on shown next to an EXISTING document upload. Lets the user let an AI read the
// document, pull out key fields, and auto-fill the form. Never mandatory; saving never depends on it.
// Props:
//  - docType: one of the backend DOC_SCHEMAS codes (or "auto")
//  - onExtract(fields, meta): called when the user taps "Use these details"
//  - fieldLabels: optional { key: "Label" } to prettify field names
//  - requiredDoc: when true, an expired document shows a stronger warning (still allowed)
//  - hint / label: optional copy overrides

const VALIDITY = {
  valid: { label: "Valid", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", Icon: CheckCircle2 },
  expiring_soon: { label: "Expiring soon", cls: "bg-amber-50 text-amber-700 ring-amber-200", Icon: AlertTriangle },
  expired: { label: "Expired", cls: "bg-rose-50 text-rose-700 ring-rose-200", Icon: AlertTriangle },
  no_expiry: { label: "No expiry on document", cls: "bg-slate-50 text-slate-500 ring-slate-200", Icon: Info },
};
const CONF = { high: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-700", low: "bg-rose-100 text-rose-700" };
const prettify = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const AIDocAssist = ({ docType = "auto", onExtract, fieldLabels = {}, requiredDoc = false,
                              label = "Auto-fill with AI", hint = "Let AI read this document and fill the details — you review before saving. Optional." }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async (file) => {
    if (!file) return;
    setBusy(true); setRes(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType || "auto");
    try {
      const r = await api.post("/ai/extract-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(r.data);
      if (!r.data?.ai) toast.message(r.data?.message || "Couldn't auto-read — please type the details.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't read the document. You can still type the details.");
    } finally { setBusy(false); }
  };

  const apply = () => {
    if (!res?.fields) return;
    onExtract && onExtract(res.fields, { validity: res.validity, docType: res.doc_type, expiry: res.expiry_date, lowConfidence: res.low_confidence });
    toast.success("Details applied — please review each field before saving.");
    setRes(null);
  };

  const entries = res?.ai ? Object.entries(res.fields || {}) : [];
  const sensitive = new Set(res?.sensitive_fields || []);
  const masked = res?.masked || {};
  const vb = res?.ai ? (VALIDITY[res.validity] || VALIDITY.no_expiry) : null;
  const expiredRequired = requiredDoc && res?.validity === "expired";

  return (
    <div className="mt-2" data-testid="ai-doc-assist">
      <input ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden"
        data-testid="ai-doc-file" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; run(f); }} />
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="ai-doc-trigger"
          className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {busy ? "Reading…" : label}
        </button>
        <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-bold text-violet-700 ring-1 ring-violet-100" data-testid="ai-doc-badge">
          <Sparkles className="h-2.5 w-2.5" /> AI
        </span>
      </div>
      <p className="mt-1 text-[11px] leading-relaxed text-slate-400">{hint}</p>

      {res && res.ai && (
        <div className="mt-2 rounded-2xl border border-violet-100 bg-violet-50/40 p-3" data-testid="ai-doc-result">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
              <ShieldCheck className="h-3.5 w-3.5 text-violet-600" /> AI read a {res.doc_type_label || "document"}
            </p>
            {vb && <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${vb.cls}`} data-testid="ai-doc-validity"><vb.Icon className="h-2.5 w-2.5" /> {vb.label}{res.days_left != null && res.validity === "expiring_soon" ? ` · ${res.days_left}d` : ""}</span>}
          </div>
          {expiredRequired && (
            <p className="mb-2 flex items-start gap-1.5 rounded-lg bg-rose-50 p-2 text-[11px] font-medium text-rose-700" data-testid="ai-doc-expired-warn">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> This document appears expired. You can still submit it, but it will be flagged for review — please upload a current copy if you have one.
            </p>
          )}
          <div className="space-y-1.5" data-testid="ai-doc-fields">
            {entries.length === 0 && <p className="text-[11px] text-slate-400">No fields could be read confidently — please type them in.</p>}
            {entries.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-2 rounded-lg bg-white px-2.5 py-1.5" data-testid={`ai-doc-field-${k}`}>
                <span className="min-w-0">
                  <span className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400">{fieldLabels[k] || prettify(k)}</span>
                  <span className="block truncate text-xs font-semibold text-slate-800">{sensitive.has(k) ? (masked[k] || v) : v}</span>
                </span>
                {res.confidence?.[k] && <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${CONF[res.confidence[k]] || "bg-slate-100 text-slate-500"}`}>{res.confidence[k]}</span>}
              </div>
            ))}
          </div>
          {res.low_confidence?.length > 0 && (
            <p className="mt-2 flex items-start gap-1 text-[10px] text-amber-600"><Info className="mt-0.5 h-3 w-3 shrink-0" /> Please double-check the fields marked LOW.</p>
          )}
          <div className="mt-2.5 flex items-center gap-2">
            <button type="button" onClick={apply} disabled={entries.length === 0} data-testid="ai-doc-apply"
              className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">Use these details</button>
            <button type="button" onClick={() => setRes(null)} data-testid="ai-doc-dismiss" className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-500 hover:bg-white"><X className="h-3 w-3" /> Ignore</button>
          </div>
          <p className="mt-2 text-[10px] leading-relaxed text-slate-400">AI reads the printed details and checks the printed expiry date only — it does not verify the document against government records. Sensitive numbers are masked here.</p>
        </div>
      )}
    </div>
  );
};

export default AIDocAssist;
